// FROM A HUBSPOT ROW BACK TO THE LEAD PAGE.
//
// Every row the /metrics mirror hands back names its company by HUBSPOT's id
// (`company_id`), and the lead page is addressed by the DIRECTORY's key
// (`/leads/company/<keyOf(row)>`). Those are two different identifiers for the
// same business, and nothing on a metrics row carries the second one — so a
// column that wants to link a response to its lead has to invert the match the
// leads table already makes.
//
// The inversion is the whole of this file, and it is pure: hand it annotated
// directory rows (lib/data.jsx has already written row._hubspot) and it hands
// back companyId -> where that lead lives. No fetching, no React, so
// scripts/test-leadLink.mjs can pin the rules under plain node.
//
// WHY IT REFUSES MORE THAN IT RESOLVES: this is a link, and a link that opens
// the wrong company is worse than no link at all — the reader cannot tell they
// have been misrouted, because the page they land on looks exactly as
// convincing as the right one. So an unmatched company, an ambiguous match, and
// two directory rows claiming the same HubSpot company all resolve to NOTHING,
// each with a sentence saying which of those happened.

import { keyOf } from './companyMeta.js'

/** Where a resolved lead lives in the app. */
export function leadHref(key) {
  const k = String(key || '')
  return k ? `/leads/company/${encodeURIComponent(k)}` : ''
}

/**
 * companyId -> { key, category, company } for every directory lead that is
 * matched, unambiguously, to exactly one HubSpot company.
 *
 * `rows` is the annotated directory: the imported leads with row._hubspot from
 * annotateRows, plus the CRM-only rows crmLeadRows() builds for companies the
 * import never had (those carry company_id 'hs-<id>', so keyOf gives them a key
 * of their own). Pass row._category on each row — the lead page needs it and
 * the directory rows do not carry it themselves.
 *
 * A company claimed by two different directory rows is recorded as a CONFLICT
 * and resolves to nothing: we hold two candidate leads and no way to tell which
 * one the reader meant, and picking the first would be a coin toss dressed as
 * an answer.
 */
export function buildLeadLinkIndex(rows) {
  const byCompany = new Map()
  const conflicts = new Set()
  for (const row of Array.isArray(rows) ? rows : []) {
    const hs = row && row._hubspot
    // `matched` is annotateRows' own verdict. An AMBIGUOUS row (a domain two
    // companies share) arrives matched:false and is skipped here for the same
    // reason it is skipped there — we do not know which company it is.
    if (!hs || !hs.matched) continue
    const id = String(hs.companyId || '')
    if (!id) continue
    const key = String(keyOf(row) || '')
    if (!key) continue
    const already = byCompany.get(id)
    if (already) {
      // Two rows, one company. Not "the first one wins" — see the file header.
      if (already.key !== key) {
        conflicts.add(id)
        byCompany.delete(id)
      }
      continue
    }
    if (conflicts.has(id)) continue
    byCompany.set(id, { key, category: String(row._category || ''), company: row })
  }
  return { byCompany, conflicts }
}

/**
 * What the Lead column should draw for one HubSpot company id.
 *
 * Returns { status, href, state, reason }:
 *   'loading'  the directory has not been read yet — say so, never a dash,
 *              because a dash here means "no such lead" and that is a claim
 *              this index cannot make until it has actually looked.
 *   'ok'       href + the router state the lead page prefers (it renders from
 *              the handed-over row instead of re-scanning every category).
 *   'none'     resolved, and there is nothing to link to. `reason` says which
 *              kind of nothing.
 */
export function leadLinkFor(index, companyId, { loading = false } = {}) {
  if (loading) return { status: 'loading', href: '', state: null, reason: 'Looking this company up in the leads directory…' }
  const id = String(companyId || '')
  if (!id) {
    return {
      status: 'none',
      href: '',
      state: null,
      // The mirror could not key this row to a company at all — it is filed
      // under a bare address — so there is no company to look up, never mind
      // to link.
      reason: 'This row is not keyed to a HubSpot company, so there is no lead page to open.',
    }
  }
  if (!index) {
    return {
      status: 'none',
      href: '',
      state: null,
      reason: 'The leads directory could not be read, so this lead is unavailable — not absent.',
    }
  }
  if (index.conflicts && index.conflicts.has(id)) {
    return {
      status: 'none',
      href: '',
      state: null,
      reason: 'More than one lead in the directory matches this HubSpot company, so this link would be a guess.',
    }
  }
  const hit = index.byCompany && index.byCompany.get(id)
  if (!hit) {
    return {
      status: 'none',
      href: '',
      state: null,
      reason: 'No lead in the directory matches this HubSpot company.',
    }
  }
  return {
    status: 'ok',
    href: leadHref(hit.key),
    // The lead page reads state.company first and only falls back to scanning
    // every category's cache — and for a CRM-only lead ('hs-<id>') that scan
    // finds nothing, because those rows are built at render time and never
    // stored. Handing the row over is what makes the link work at all for
    // them, and instant for everyone else.
    state: { company: hit.company, category: hit.category },
    reason: '',
  }
}
